/**
 * usePlayAds.tsx
 * Custom hook that manages play ad display logic.
 * Fetches and displays play ads when the player is playing, and clears the ad state when the player is not playing.
 *
 * @author Mark van der Heijden
 * @version 1.0.0
 * @since 2026-04-28
 */
import { PlayerState } from '@shared/types/enums/player';
import { mediaSelector } from '@shared/store/slices/media.slice';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useEffect } from 'react';
import {
  adsSelector,
  clearPlayAd,
  setPlayAd,
} from '@shared/store/slices/ads.slice';

/**
 * Constants.
 *
 * @constant {number} INITIAL_DELAY - The initial delay before the first ad is displayed
 * @constant {number} PLAY_AD_INTERVAL - The interval at which ads are displayed
 */
const INITIAL_DELAY = 1 * 60 * 1000; // 1 minute
const PLAY_AD_INTERVAL = 15 * 60 * 1000; // 15 minutes

/**
 * Custom hook that manages pause ad display logic.
 * Fetches and displays pause ads when the player is paused, and clears the modal when playback resumes.
 *
 * @param {Event} [asset] - Optional event asset to display pause ad information for
 */
export default function usePlayAds({
  togglePilot,
}: {
  togglePilot?: (display: boolean) => void;
}) {
  const dispatch = useAppDispatch();
  const { playAd } = useAppSelector(adsSelector);
  const { position, state } = useAppSelector(mediaSelector);

  useEffect(() => {
    if (state === PlayerState.PLAYING) {
      const currentDate = new Date();
      const nextDate = playAd?.nextDate ? new Date(playAd.nextDate) : undefined;
      // If no next date, set the initial delay
      if (!nextDate) {
        dispatch(
          setPlayAd({
            active: false,
            nextDate: new Date(currentDate.getTime() + INITIAL_DELAY),
          })
        );
      }
      // If the current date is greater than the next date, set the next date to the next interval
      else if (currentDate > nextDate) {
        dispatch(
          setPlayAd({
            active: true,
            nextDate: new Date(currentDate.getTime() + PLAY_AD_INTERVAL),
          })
        );
        togglePilot?.(false);
      }
    } else {
      dispatch(
        setPlayAd({
          active: false,
        })
      );
    }
  }, [position, state]);

  // Clear ad state when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearPlayAd());
    };
  }, []);
}
